import { Form } from "apps/visits/packages/ui/src/components";
import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { cn } from "@/core/shared/v2/lib/utils";
import { Checkbox } from "@/core/shared/v2/ui/checkbox";
import type { FormValues } from "../../user-data-form/validation-schema";

type ConsentsSectionProps = {
  showMarketingConsent: boolean;
  isSelectAllChecked: boolean;
  consentFieldsForSelectAll: readonly (keyof FormValues)[];
  isGuestCheckout: boolean;
  privacyPolicyHref: string;
  termsHref: string;
};

export const ConsentsSection = ({
  showMarketingConsent,
  isSelectAllChecked,
  consentFieldsForSelectAll,
  isGuestCheckout,
  privacyPolicyHref,
  termsHref,
}: ConsentsSectionProps) => {
  const { t } = useTranslation();
  const { control, register, setValue, errors } = useFormContext<FormValues>();
  const [marketingDetailsValue, setMarketingDetailsValue] = useState("");
  const isMarketingDetailsOpen = marketingDetailsValue === "details";

  return (
    <div>
      {/* Hidden inputs register the mirror consent fields so setValue()
          on them writes through to RHF state (yup validation, submit
          payload). Visual sync of the Select-all checkbox is handled
          by deriving it from the two master Controllers above — those
          are the fields that watch() reliably re-renders on. */}
      <input type="checkbox" name="acceptDataProcessing" ref={register} className="hidden" tabIndex={-1} aria-hidden />
      <input type="checkbox" name="acceptNewsletter" ref={register} className="hidden" tabIndex={-1} aria-hidden />
      {showMarketingConsent && (
        <div className="mb-4 w-fit">
          <div className="flex items-center gap-2" data-testid="select-all-consents">
            <Checkbox
              id="selectAll"
              checked={isSelectAllChecked}
              onCheckedChange={(checked) => {
                consentFieldsForSelectAll.forEach((field) => {
                  setValue(field, !!checked, { shouldValidate: true });
                });
              }}
            />
            <Form.Label htmlFor="selectAll" className="-ml-2 pt-1.5 pb-0 font-semibold text-muted-foreground">
              <span className="text-base">{t("VOUCHERS_DISTRIBUTION.FREE_PACKAGES.SELECT_ALL")}</span>
            </Form.Label>
          </div>
        </div>
      )}
      <div className={cn("flex flex-col gap-3", showMarketingConsent && "pl-4")}>
        <Controller
          control={control}
          name="acceptTouNPp"
          render={({ onChange, value, ref }) => (
            <div className="flex items-start gap-2">
              <Checkbox
                ref={ref}
                id="acceptObligatoryConsent"
                size={showMarketingConsent ? "sm" : "default"}
                checked={!!value}
                onCheckedChange={(checked) => {
                  onChange(!!checked);
                  setValue("acceptDataProcessing", !!checked, { shouldValidate: true });
                }}
              />
              <div className="flex flex-col">
                <Form.Label
                  htmlFor="acceptObligatoryConsent"
                  className="-ml-2 pt-1.5 pb-0 font-normal text-muted-foreground"
                >
                  <span className={showMarketingConsent ? "text-[13px]" : "text-sm"}>
                    <Trans
                      i18nKey="VOUCHERS_DISTRIBUTION.USER_DATA_FORM.OBLIGATORY_CONSENT"
                      components={{
                        linkPolicies: (
                          // biome-ignore lint/a11y/useAnchorContent: content provided by Trans i18n component
                          <a
                            href={privacyPolicyHref}
                            className="text-dodger-blue-700 underline"
                            target="_blank"
                            rel="noopener"
                            aria-label="Privacy policy"
                          />
                        ),
                        linkRules: (
                          // biome-ignore lint/a11y/useAnchorContent: content provided by Trans i18n component
                          <a
                            href={termsHref}
                            className="text-dodger-blue-700 underline"
                            target="_blank"
                            rel="noopener"
                            aria-label="Terms and conditions"
                          />
                        ),
                      }}
                    />
                  </span>
                </Form.Label>
                {(errors.acceptDataProcessing?.message || errors.acceptTouNPp?.message) && (
                  <p className="font-medium text-destructive text-sm">
                    {errors.acceptDataProcessing?.message || errors.acceptTouNPp?.message}
                  </p>
                )}
              </div>
            </div>
          )}
        />

        {showMarketingConsent && (
          <Controller
            control={control}
            name="acceptMarketingPhoneContact"
            render={({ onChange, value, ref }) => (
              <div className="flex items-start gap-2">
                <Checkbox
                  ref={ref}
                  id="acceptMarketingConsent"
                  size="sm"
                  checked={!!value}
                  onCheckedChange={(checked) => {
                    onChange(!!checked);
                    if (!isGuestCheckout) {
                      setValue("acceptNewsletter", !!checked, { shouldValidate: true });
                    }
                  }}
                />
                <div className="flex flex-col">
                  <Form.Label
                    htmlFor="acceptMarketingConsent"
                    className="-ml-2 pt-1.5 pb-0 font-normal text-muted-foreground"
                  >
                    <span className="text-[13px]">
                      {t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.MARKETING_CONSENT")}{" "}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setMarketingDetailsValue((v) => (v === "details" ? "" : "details"));
                        }}
                        className="text-dodger-blue-700 underline"
                      >
                        {isMarketingDetailsOpen
                          ? t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.MARKETING_CONSENT_COLLAPSE")
                          : t("VOUCHERS_DISTRIBUTION.USER_DATA_FORM.MARKETING_CONSENT_EXPAND")}
                      </button>
                    </span>
                  </Form.Label>
                  {isMarketingDetailsOpen && (
                    <div className="mt-1 -ml-2 text-muted-foreground text-sm">
                      <Trans
                        i18nKey="VOUCHERS_DISTRIBUTION.USER_DATA_FORM.MARKETING_CONSENT_DETAILS"
                        components={{
                          paragraph: <p className="mb-2 last:mb-0" />,
                          bold: <strong className="font-semibold" />,
                          linkPrivacyPolicy: (
                            // biome-ignore lint/a11y/useAnchorContent: content provided by Trans i18n component
                            <a
                              href={privacyPolicyHref}
                              className="text-dodger-blue-700 underline"
                              target="_blank"
                              rel="noopener"
                              aria-label="Privacy policy"
                            />
                          ),
                          linkNewsletterTerms: (
                            // biome-ignore lint/a11y/useAnchorContent: content provided by Trans i18n component
                            <a
                              href="https://upacjenta.pl/download/newsletter-terms-and-conditions"
                              className="text-dodger-blue-700 underline"
                              target="_blank"
                              rel="noopener"
                              aria-label="Newsletter terms"
                            />
                          ),
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          />
        )}
      </div>
    </div>
  );
};
